import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';


import { useActions } from '../../../Actions';
import { useAccountId } from '../../../auth/useAccountId';

import { State } from '../../../reducer';
import { namespace } from '../../transactionDetails';
import { namespace as messagesNamespace } from '../../../messages';
import { Transaction } from '../../../types/checkout';
import { NewOperationItem } from '../../types';
import { getTransactionItems } from '../helpers';
import { getDefaultFeeSplit } from '../../splitPaymentHelpers';
import { hasCorrection, hasNegativeLineItem, hasLineIds, previousOperationsHasItems, getRefundableAmount, hasRefundableState, previousOperationsAreConsistent } from '../../transactionHelpers';
import { formatMoney } from '../../../helpers/formatters';

import Page, { CustomBack, Title } from '../../../components/Page';
import Card from '../../../components/Card';
import { H2 } from '../../../components/Typography';
import RefundByAmount from './RefundByAmount';
import RefundByItems from './RefundByItems';
import NotFound from '../../transactionDetails/components/NotFound';
import { LoadingOverlay } from '../../../components/Loading';
import RefundError from './RefundError';


const RefundTransaction = () => {

    const { t, i18n } = useTranslation();
    const accountId = useAccountId();
    const transactionId = useParams<{transactionId: string}>().transactionId;
    const { refundTransaction, setView, closeErrorModal } = useActions(namespace);
    const { transaction, session, isLoading, isNotFound, error } = useSelector<State, State[typeof namespace]>(
        (s) => s[namespace]
    );
    const { addMessage } = useActions(messagesNamespace);
    const showSuccessMessage = (refundedAmount: number, transaction: Transaction) => {
        const events = transaction.events || [];
        const event = events[events.length - 1].event;
        const initiateRefund = event === 'INITIATE_REFUND';
        addMessage({
            text: t(initiateRefund ? 'payments.refund.success.initiate_title' : 'payments.refund.success.title', {
                amount: formatMoney(i18n.language, refundedAmount, transaction.currency),
            }),
            severity: 'success',
        });
    };

    const items = (transaction && session  ? getTransactionItems(transaction, session).map(item => ({
        persisted: item,
        operation: {
            amount: item.refundableAmount,
            quantity: item.refundableQuantity,
            splits: item.refundableSplits,
            feeSplit: getDefaultFeeSplit(item.refundableSplits),
        },
    })) : []) as NewOperationItem[];

    const showItems = transaction &&
        session &&
        items.length > 0 &&
        !session?.order?.partial_payment &&
        !hasCorrection(transaction) &&
        !hasNegativeLineItem(transaction) &&
        hasLineIds(transaction) &&
        previousOperationsHasItems(transaction) &&
        previousOperationsAreConsistent(transaction);

    if (
        transaction &&
        ((getRefundableAmount(transaction) <= 0 &&
            transaction.payment_product_type !== 'dintero.zero') ||
            !hasRefundableState(transaction))
    ) {
        return null;
    }

    return <Page
        title={<><CustomBack onClick={() => setView('details')} /><Title>{t('payments.refund.form.title')}</Title></>}
        hasEnvFrame={true}
    >
        <Card noPadding={isNotFound}>
            <H2>{transaction?.merchant_reference || transaction?.id}</H2>

            {transaction && showItems &&
                <RefundByItems
                    transaction={transaction}
                    items={items}
                    refundTransaction={refundTransaction}
                    accountId={accountId}
                    isLoading={isLoading}
                    showSuccessMessage={showSuccessMessage}
                />
            }
            {transaction && !showItems &&
                <RefundByAmount
                    transaction={transaction}
                    refundTransaction={refundTransaction}
                    accountId={accountId}
                    isLoading={isLoading}
                    showSuccessMessage={showSuccessMessage}
                />
            }
            {isLoading && <LoadingOverlay />}
            {isNotFound && <NotFound transactionId={transactionId} />}
        </Card>
        {error && <RefundError error={error} transaction={transaction} dismissModal={closeErrorModal} />}
    </Page>;
};

export default RefundTransaction;
